import { UnitType, type InventoryItemType } from '@/lib/shared-types';

/**
 * X-16 — the shape and the rules behind the inventory *definition* forms
 * (items, units, warehouses), kept out of the components so they can be tested
 * without a DOM.
 *
 * Everything here mirrors a constraint that already exists on the server or in
 * `schema.prisma`. Where the server does NOT enforce something, that is said
 * out loud rather than quietly enforced here — a client-side rule the API does
 * not share is a rule that breaks the moment anything else writes.
 */

/** What `IsDecimalString` accepts on the wire (`IsNumberString`). */
export const DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Column scales, read from `schema.prisma` rather than assumed.
 *
 * These are NOT money. `inventory_items.cost_price` is `Decimal(12,4)` — a cost
 * *per stock unit*, and a gram of flour costs 0.0020, which is why four places
 * exist and why rounding it to a currency's two would zero it. `useCurrency()`
 * formats at the currency's minor unit and is right for a total (a movement's
 * `totalCost`, a valuation) and wrong for this field.
 */
export const COST_SCALE = 4;
/** `reorder_point` / `reorder_quantity` are `Decimal(12,3)`, like every quantity. */
export const QUANTITY_SCALE = 3;
/** `units.factor_to_base` is `Decimal(18,6)`. */
export const FACTOR_SCALE = 6;

/** Why a typed decimal would be refused. Codes, so the caller owns the copy. */
export type DecimalProblem = 'invalid' | 'scale' | 'negative' | 'positive';

export function decimalProblem(
  raw: string,
  options: { scale: number; sign?: 'any' | 'non-negative' | 'positive' },
): DecimalProblem | null {
  const text = raw.trim();
  if (!DECIMAL.test(text)) return 'invalid';
  const [, fraction = ''] = text.split('.');
  if (fraction.length > options.scale) return 'scale';
  const value = Number(text);
  if (options.sign === 'positive' && !(value > 0)) return 'positive';
  if (options.sign === 'non-negative' && value < 0) return 'negative';
  return null;
}

// ------------------------------------------------------------------ units

export interface UnitRow {
  id: string;
  code: string;
  name: string;
  type: UnitType;
  baseUnitId: string | null;
  factorToBase: string;
  isActive: boolean;
}

/**
 * `unit` plus everything that converts into it, directly or through a chain.
 *
 * These are the units that may never become `unit`'s own base. `UnitsService`
 * guards only the one-step case (`dto.baseUnitId === id`); a two-step cycle
 * saves happily and is not discovered until `rootOf()` walks it during a
 * conversion and throws "circular base-unit chain" — by then the recipe that
 * triggered it is what fails, not the unit that caused it. So the picker
 * simply does not offer them.
 */
export function descendantsOf(units: UnitRow[], id: string): Set<string> {
  const found = new Set<string>([id]);
  // Fixed-point rather than recursion: a chain already corrupted by a cycle
  // must not hang the form that is trying to keep it from getting worse.
  for (let pass = 0; pass < units.length; pass += 1) {
    let grew = false;
    for (const unit of units) {
      if (!found.has(unit.id) && unit.baseUnitId && found.has(unit.baseUnitId)) {
        found.add(unit.id);
        grew = true;
      }
    }
    if (!grew) break;
  }
  return found;
}

/**
 * What may legally be picked as a base unit for a unit of `type`.
 *
 * `assertConvertible` refuses a base of a different `UnitType` outright, so the
 * type filter is the server's rule, not a nicety.
 */
export function baseUnitOptions(
  units: UnitRow[],
  type: UnitType,
  editingId?: string | null,
): UnitRow[] {
  const excluded = editingId ? descendantsOf(units, editingId) : new Set<string>();
  return units.filter((unit) => unit.type === type && !excluded.has(unit.id));
}

/** The units of `type` that are their own reference point (`baseUnitId` null). */
export function rootsOfType(units: UnitRow[], type: UnitType, exceptId?: string | null): UnitRow[] {
  return units.filter(
    (unit) => unit.type === type && unit.baseUnitId === null && unit.id !== exceptId,
  );
}

export interface UnitDraft {
  code: string;
  name: string;
  type: UnitType;
  baseUnitId: string;
  factorToBase: string;
  isActive: boolean;
}

export const EMPTY_UNIT: UnitDraft = {
  code: '',
  name: '',
  type: UnitType.COUNT,
  baseUnitId: '',
  factorToBase: '',
  isActive: true,
};

export type UnitProblem =
  | { field: 'code'; code: 'required' }
  | { field: 'name'; code: 'required' }
  | { field: 'factorToBase'; code: DecimalProblem | 'required' };

/** What would make the API refuse this unit, in the order it checks. */
export function unitProblem(draft: UnitDraft): UnitProblem | null {
  if (!draft.code.trim()) return { field: 'code', code: 'required' };
  if (!draft.name.trim()) return { field: 'name', code: 'required' };
  if (draft.baseUnitId) {
    if (!draft.factorToBase.trim()) return { field: 'factorToBase', code: 'required' };
    const problem = decimalProblem(draft.factorToBase, {
      scale: FACTOR_SCALE,
      sign: 'positive',
    });
    // A factor of zero is accepted by `IsNumberString` and then divides by zero
    // inside `convertWith`; the server has no guard for it.
    if (problem) return { field: 'factorToBase', code: problem };
  }
  return null;
}

/**
 * The trap this form exists to close.
 *
 * A unit with no `baseUnitId` is a *root*: its factor is forced to 1 and it is
 * its own measurement family. `convertWith` refuses any pair that does not
 * share a root — "do not share a base unit, so no factor exists" — so a second
 * root of a type that already has one produces a unit that looks fine in every
 * list and then fails the first time a recipe or a stock movement tries to
 * express a quantity in it against an item stocked in the other family.
 *
 * Returns the roots the draft would be cut off from, or an empty array when it
 * is safe (it converts into something, or it is the first root of its type).
 */
export function isolatedFrom(
  units: UnitRow[],
  draft: UnitDraft,
  editingId?: string | null,
): UnitRow[] {
  if (draft.baseUnitId) return [];
  return rootsOfType(units, draft.type, editingId);
}

/**
 * The unit's whole chain folded to one multiplier, so the form can show what it
 * is about to mean: "1 box = 24 g". Mirrors `UnitsService.rootOf`, including
 * its refusal to walk forever, and returns null when the chain is broken.
 */
export function foldToRoot(
  units: UnitRow[],
  startBaseUnitId: string,
  factorToBase: string,
): { root: UnitRow; factor: number } | null {
  const byId = new Map(units.map((unit) => [unit.id, unit]));
  const seed = Number(factorToBase);
  if (!Number.isFinite(seed)) return null;

  let factor = seed;
  let current = byId.get(startBaseUnitId);
  if (!current) return null;
  const seen = new Set<string>([startBaseUnitId]);

  factor *= Number(current.factorToBase);
  while (current.baseUnitId) {
    if (seen.has(current.baseUnitId)) return null;
    seen.add(current.baseUnitId);
    const next = byId.get(current.baseUnitId);
    if (!next) return null;
    current = next;
    factor *= Number(current.factorToBase);
  }
  return Number.isFinite(factor) ? { root: current, factor } : null;
}

// ------------------------------------------------------------- warehouses

export interface WarehouseRow {
  id: string;
  name: string;
  code: string;
  branchId: string | null;
  isDefault: boolean;
  isActive: boolean;
  branch?: { id: string; name: string; code: string } | null;
}

export interface WarehouseDraft {
  name: string;
  code: string;
  branchId: string;
  isDefault: boolean;
  isActive: boolean;
}

export const EMPTY_WAREHOUSE: WarehouseDraft = {
  name: '',
  code: '',
  branchId: '',
  isDefault: false,
  isActive: true,
};

export type WarehouseProblem = { field: 'name' | 'code'; code: 'required' };

export function warehouseProblem(draft: WarehouseDraft): WarehouseProblem | null {
  if (!draft.name.trim()) return { field: 'name', code: 'required' };
  if (!draft.code.trim()) return { field: 'code', code: 'required' };
  return null;
}

/**
 * The body for `POST /inventory/warehouses`.
 *
 * `forbidNonWhitelisted` is on, so an empty string is not a harmless blank: it
 * is a field the DTO will reject or, worse, accept as a branch id that does not
 * exist. Omit rather than blank.
 */
export function warehouseCreateBody(draft: WarehouseDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    code: draft.code.trim(),
    ...(draft.branchId ? { branchId: draft.branchId } : {}),
    isDefault: draft.isDefault,
    isActive: draft.isActive,
  };
}

/**
 * The body for `PATCH /inventory/warehouses/:id`.
 *
 * `UpdateWarehouseDto` is `PartialType(OmitType(CreateWarehouseDto, ['branchId']))`
 * — branch is the warehouse's anchor and is deliberately not editable. Sending
 * it anyway is a 400 under `forbidNonWhitelisted`, so it is dropped here and
 * the form renders the branch read-only rather than offering a control that
 * could only fail.
 */
export function warehouseUpdateBody(draft: WarehouseDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    code: draft.code.trim(),
    isDefault: draft.isDefault,
    isActive: draft.isActive,
  };
}

/**
 * How a warehouse's branch should read.
 *
 * `null` branchId is a tenant-wide store and prints as such. A branchId whose
 * branch did not come back is NOT the same thing and must not print as
 * "tenant-wide": demo-cafe's `RTWH1` is anchored to a branch of another tenant
 * entirely, and RLS correctly hides that row, so the include lands as null.
 * Saying "unknown branch" is the difference between showing the operator a
 * misconfiguration and hiding one.
 */
export type BranchLabel =
  | { kind: 'tenant-wide' }
  | { kind: 'named'; name: string }
  | { kind: 'unreadable'; id: string };

export function branchLabel(warehouse: WarehouseRow): BranchLabel {
  if (!warehouse.branchId) return { kind: 'tenant-wide' };
  if (warehouse.branch?.name) return { kind: 'named', name: warehouse.branch.name };
  return { kind: 'unreadable', id: warehouse.branchId };
}

// ------------------------------------------------------------------ items

export interface ItemDraft {
  name: string;
  unitId: string;
  type: InventoryItemType;
  catalogItemId: string;
  sku: string;
  barcode: string;
  description: string;
  costPrice: string;
  reorderPoint: string;
  reorderQuantity: string;
  isActive: boolean;
}

export type ItemProblem =
  | { field: 'name' | 'unitId'; code: 'required' }
  | { field: 'costPrice' | 'reorderPoint' | 'reorderQuantity'; code: DecimalProblem };

export function itemProblem(draft: ItemDraft): ItemProblem | null {
  if (!draft.name.trim()) return { field: 'name', code: 'required' };
  if (!draft.unitId) return { field: 'unitId', code: 'required' };

  if (draft.costPrice.trim()) {
    const problem = decimalProblem(draft.costPrice, {
      scale: COST_SCALE,
      sign: 'non-negative',
    });
    if (problem) return { field: 'costPrice', code: problem };
  }
  for (const field of ['reorderPoint', 'reorderQuantity'] as const) {
    if (!draft[field].trim()) continue;
    const problem = decimalProblem(draft[field], {
      scale: QUANTITY_SCALE,
      sign: 'non-negative',
    });
    if (problem) return { field, code: problem };
  }
  return null;
}

/**
 * The body for both `POST` and `PATCH /inventory/items`.
 *
 * Optional text fields are omitted when blank rather than sent as `''`: an
 * empty `sku` would take part in the `@@unique([tenantId, sku])` hand-match in
 * `assertSkuFree` and collide with the next item saved the same way.
 */
export function itemBody(draft: ItemDraft): Record<string, unknown> {
  const text = (value: string) => (value.trim() ? { value: value.trim() } : null);
  const optional: Record<string, unknown> = {};
  for (const field of ['sku', 'barcode', 'description', 'catalogItemId'] as const) {
    const kept = text(draft[field]);
    if (kept) optional[field] = kept.value;
  }
  for (const field of ['costPrice', 'reorderPoint', 'reorderQuantity'] as const) {
    const kept = text(draft[field]);
    if (kept) optional[field] = kept.value;
  }
  return {
    name: draft.name.trim(),
    unitId: draft.unitId,
    type: draft.type,
    isActive: draft.isActive,
    ...optional,
  };
}
